import {
  BarChart3,
  CalendarClock,
  ClipboardList,
  FileSearch,
  FileText,
  GitCompare,
  History,
  MapPin,
  RefreshCw,
  Search,
} from "lucide-react";
import { useLocation, useParams } from "react-router-dom";
import { useAuth } from "@/client/hooks/use-auth";

interface WelcomeStateProps {
  onAction: (text: string) => void;
}

interface QuickAction {
  label: string;
  prompt: string;
  icon: React.ReactNode;
}

const DASHBOARD_ACTIONS: QuickAction[] = [
  {
    label: "What's expiring soon?",
    prompt: "What records are expiring in the next 30 days?",
    icon: <CalendarClock className="h-4 w-4" />,
  },
  {
    label: "Portfolio summary",
    prompt: "Give me an overview of the full portfolio — total records, values, and any issues",
    icon: <BarChart3 className="h-4 w-4" />,
  },
  {
    label: "Search for a property",
    prompt: "I need to find a property record. Can you help me search?",
    icon: <Search className="h-4 w-4" />,
  },
  {
    label: "Who's overloaded?",
    prompt: "Show me the valuer workload breakdown — who has the most records?",
    icon: <ClipboardList className="h-4 w-4" />,
  },
  {
    label: "Compare two records",
    prompt: "I want to compare two insurance records side by side",
    icon: <GitCompare className="h-4 w-4" />,
  },
  {
    label: "Search by suburb",
    prompt: "Search for all records in a specific suburb or area",
    icon: <MapPin className="h-4 w-4" />,
  },
];

const RECORD_VIEW_ACTIONS: QuickAction[] = [
  {
    label: "Summarise this record",
    prompt: "Summarise insurance record #__ID__",
    icon: <FileText className="h-4 w-4" />,
  },
  {
    label: "Download PDF",
    prompt: "Generate the PDF certificate for record #__ID__",
    icon: <FileSearch className="h-4 w-4" />,
  },
  {
    label: "Audit history",
    prompt: "Show me the full audit history for record #__ID__",
    icon: <History className="h-4 w-4" />,
  },
  {
    label: "Compare versions",
    prompt: "Has record #__ID__ been superseded? Compare it with the previous version",
    icon: <GitCompare className="h-4 w-4" />,
  },
  {
    label: "Start renewal",
    prompt: "I want to start the renewal process for record #__ID__",
    icon: <RefreshCw className="h-4 w-4" />,
  },
  {
    label: "Find similar records",
    prompt: "Find records similar to #__ID__ — same area or property type",
    icon: <Search className="h-4 w-4" />,
  },
];

const LIST_ACTIONS: QuickAction[] = [
  {
    label: "What's expiring soon?",
    prompt: "Which records are expiring in the next 30 days?",
    icon: <CalendarClock className="h-4 w-4" />,
  },
  {
    label: "Search for a property",
    prompt: "Help me find a specific property record",
    icon: <Search className="h-4 w-4" />,
  },
  {
    label: "Portfolio summary",
    prompt: "Give me a quick overview of the portfolio health",
    icon: <BarChart3 className="h-4 w-4" />,
  },
  { label: "Search by suburb", prompt: "Find all records in a specific suburb", icon: <MapPin className="h-4 w-4" /> },
];

function getFirstName(fullName: string): string {
  return fullName.split(" ")[0] ?? "there";
}

export function WelcomeState({ onAction }: WelcomeStateProps): React.ReactElement {
  const { user } = useAuth();
  const location = useLocation();
  const params = useParams<{ id: string }>();

  const firstName = user ? getFirstName(user.name) : "there";
  const recordId = params.id;
  const isRecordView = location.pathname.match(/^\/insurance\/\d+$/) && recordId;
  const isListView = location.pathname === "/insurance";

  let actions: QuickAction[];
  let contextHint: string;

  if (isRecordView && recordId) {
    actions = RECORD_VIEW_ACTIONS.map((a) => ({
      ...a,
      prompt: a.prompt.replace(/__ID__/g, recordId),
    }));
    contextHint = `I can see you're viewing record #${recordId}. Here are some things I can help with:`;
  } else if (isListView) {
    actions = LIST_ACTIONS;
    contextHint = "I can help you search, filter, and understand your records:";
  } else {
    actions = DASHBOARD_ACTIONS;
    contextHint = "Here are some things I can help with:";
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center p-5" data-testid="agent-welcome">
      <div className="w-full max-w-sm space-y-4">
        <div className="text-center space-y-1">
          <h2 className="text-base font-semibold text-foreground">Hi {firstName}, how can I help?</h2>
          <p className="text-xs text-muted-foreground">{contextHint}</p>
        </div>

        <div className="grid gap-2">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={() => onAction(action.prompt)}
              className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 text-left text-sm hover:bg-accent hover:border-primary/20 transition-colors group cursor-pointer"
              data-testid="agent-quick-action"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
                {action.icon}
              </div>
              <span className="text-foreground font-medium">{action.label}</span>
            </button>
          ))}
        </div>

        <p className="text-center text-[11px] text-muted-foreground/60">
          Or just type your question below — I understand plain English.
        </p>
      </div>
    </div>
  );
}
